import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, MessageSquare, ClipboardList, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Floating notification bubbles — shows pending (undismissed) mensajes.
 * Polls /api/mensajes/pending every 30s. Persist until user closes with X.
 */
export default function NotificationBubbles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const fetchPending = async () => {
    try {
      const r = await api.get("/mensajes/pending");
      setItems(r.data || []);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    if (!user) return;
    fetchPending();
    const id = setInterval(fetchPending, 30000);
    // Listen to a custom event to force refresh (e.g., after creating a solicitud)
    const handler = () => fetchPending();
    window.addEventListener("mensajes:refresh", handler);
    return () => { clearInterval(id); window.removeEventListener("mensajes:refresh", handler); };
  }, [user]);

  const dismiss = async (m) => {
    try {
      await api.post(`/mensajes/${m.id}/dismiss`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
    } catch (_) { /* silent */ }
  };

  const openSolicitud = (m) => {
    if (m.solicitud_id) navigate("/solicitudes");
    else navigate("/mensajes");
  };

  if (!user || items.length === 0) return null;

  return (
    <div
      data-testid="notification-bubbles"
      className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 max-w-sm"
    >
      {items.slice(0, 5).map((m) => {
        const isAsig = m.tipo === "asignacion";
        return (
          <div
            key={m.id}
            data-testid={`bubble-${m.id}`}
            className="animate-in slide-in-from-right-5 rounded-2xl bg-white border border-[#E5DFD3] shadow-[0_12px_28px_-8px_rgba(0,0,0,0.18)] overflow-hidden"
            style={{ borderLeft: `4px solid ${isAsig ? "#7A1E2E" : "#4A6D8C"}` }}
          >
            <div className="p-4 pr-9 relative">
              <button
                data-testid={`bubble-close-${m.id}`}
                onClick={() => dismiss(m)}
                className="absolute top-2 right-2 h-7 w-7 rounded-full hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E] transition-colors"
                title="Cerrar"
              >
                <X className="h-4 w-4" strokeWidth={2}/>
              </button>
              <div className="flex items-start gap-3">
                <div
                  className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: isAsig ? "#FBEDEF" : "#DDE9F3" }}
                >
                  {isAsig
                    ? <ClipboardList className="h-4 w-4" strokeWidth={1.8} style={{ color: "#7A1E2E" }}/>
                    : <MessageSquare className="h-4 w-4" strokeWidth={1.8} style={{ color: "#4A6D8C" }}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B5D57]">
                    {isAsig ? "Nueva asignación" : "Mensaje interno"} · de {m.remitente_nombre}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-[#1A0810] leading-snug">{m.titulo}</div>
                  {m.contenido && <div className="mt-1 text-xs text-[#4a3838] leading-relaxed line-clamp-3">{m.contenido}</div>}
                  <button
                    onClick={() => openSolicitud(m)}
                    className="mt-2 text-[11px] uppercase tracking-wider font-semibold text-[#7A1E2E] hover:underline"
                  >
                    {m.solicitud_id ? "Ver solicitud →" : "Ir a Mensajes →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {items.length > 5 && (
        <div className="text-center text-[11px] text-[#6B5D57]">
          <Bell className="h-3 w-3 inline mr-1"/> +{items.length - 5} más pendientes en Mensajes
        </div>
      )}
    </div>
  );
}
